import React from 'react';
import SEOHead from '../components/SEOHead';
import './Terms.css';

const Terms = () => {
  const lastUpdated = "July 24, 2025";

  const sections = [
    {
      title: "Introduction",
      content: [
        'OneTimer ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we handle information in connection with your use of the OneTimer app (the "App"). The App is a simple timer designed to help users track focused time on their passions or obsessions, with all user-generated data stored locally on your device.',
        "We do not collect, store, or share any personal information from users through the App itself. However, we use a third-party service, RevenueCat, to manage in-app purchases and subscriptions, which may involve the processing of certain data on our behalf.",
        "By using the App, you agree to the practices described in this Privacy Policy. If you do not agree, please do not use the App."
      ]
    },
    {
      title: "Information We Collect",
      content: [
        {
          subtitle: "No Personal Data Collected by the App:",
          text: "The App does not collect any personal information, such as your name, email address, device ID, location, or any other identifiers. We do not track your usage for analytics, advertising, or any other purposes."
        },
        {
          subtitle: "Local Storage:",
          text: "All timer data, progress statistics (e.g., daily totals, averages, heatmaps), and any other information you input or generate within the App is stored locally on your device using asynchronous storage. This data is not transmitted to our servers or any third parties by the App itself. It remains on your device and is accessible only to you. If you delete the App or clear your device storage, this data may be lost."
        },
        {
          subtitle: "Third-Party Services (RevenueCat):",
          text: "To handle in-app purchases and subscriptions, we use RevenueCat, a third-party service. RevenueCat acts as a data processor on our behalf and may collect and process the following information when you make a purchase:",
          list: [
            "Device type and operating system.",
            "Transaction details, such as Apple receipt files or purchase tokens.",
            "Timestamps of when you last used the App (for subscription management).",
            "Optional metadata or attribution information (if enabled, though we do not enable this)."
          ],
          additionalText: 'This data is used solely to facilitate and manage your purchases and subscriptions. RevenueCat does not collect personally identifiable information like your name or email unless you provide it directly (which the App does not require). For more details on RevenueCat\'s data practices, please review their Privacy Policy at https://www.revenuecat.com/privacy.'
        }
      ]
    },
    {
      title: "How We Use Information",
      content: [
        "Since the App collects no personal data, there is no usage of such data by us. Local data is used only within the App to display your timer progress and statistics.",
        "Any data processed by RevenueCat is used exclusively for processing purchases, verifying subscriptions, and providing related services."
      ]
    },
    {
      title: "Sharing of Information",
      content: [
        "We do not share any data from the App with advertisers, partners, or third parties, except as described for RevenueCat in connection with purchases.",
        "RevenueCat may share the processed data with their subprocessors (e.g., for hosting or payments) under strict data processing agreements. We do not control RevenueCat's sharing practices beyond our agreement with them, but they commit to not selling or misusing the data.",
        "In rare cases, we may disclose information if required by law, to enforce our rights, or to protect the safety of users or the public."
      ]
    },
    {
      title: "Data Security",
      content: [
        "We take reasonable measures to protect any data handled through the App. Local data on your device is secured by your device's built-in security features. For purchase-related data, RevenueCat employs industry-standard security practices, including encryption (TLS/SSL) and regular audits.",
        "However, no method of transmission or storage is completely secure, so we cannot guarantee absolute security."
      ]
    },
    {
      title: "Children's Privacy",
      content: [
        "The App is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are under 13, please do not use the App. The App is primarily targeted at users aged 16-30. We comply with applicable laws, including the Children's Online Privacy Protection Act (COPPA), by not collecting any data from minors."
      ]
    },
    {
      title: "International Data Transfers",
      content: [
        "If you are located outside the United States, note that any purchase-related data processed by RevenueCat is transferred to and stored in the USA, which may have different data protection laws. By using the App, you consent to this transfer."
      ]
    },
    {
      title: "Your Rights",
      content: [
        "Since we do not collect personal data, rights such as access, deletion, or correction do not apply to data held by us. For any data processed by RevenueCat, you can exercise rights under applicable laws (e.g., GDPR for EU residents) by contacting us, and we will forward your request to RevenueCat as needed. This may include rights to access, rectify, erase, or port your data."
      ]
    },
    {
      title: "Changes to This Privacy Policy",
      content: [
        "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy in the App or on our support page. Your continued use of the App after changes constitutes acceptance of the updated policy."
      ]
    },
    {
      title: "Contact Us",
      content: [
        "If you have questions about this Privacy Policy or our practices, please contact us at theonetimerapp@gmail.com or via our contact page.",
        "This Privacy Policy applies only to the App and does not cover third-party sites or services linked from the App (if any)."
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="Privacy Policy & Terms - OneTimer"
        description="OneTimer privacy policy and terms of service. Learn how we protect your data and what information we collect."
        keywords="OneTimer privacy policy, terms of service, data protection, app privacy"
        path="/terms"
      />
      <div className="terms-container">
      <div className="terms-header">
        <h1>Terms & Privacy</h1>
        <p className="subtitle">How we handle your information</p>
        <div className="last-updated">Last Updated: {lastUpdated}</div>
      </div>
      
      <div className="terms-content">
        {sections.map((section, index) => (
          <div key={index} className="section">
            <h2>{section.title}</h2>
            {section.content.map((item, itemIndex) => {
              if (typeof item === 'string') {
                return <p key={itemIndex}>{item}</p>;
              } else if (item.subtitle) {
                return (
                  <div key={itemIndex} className="subsection">
                    <p><strong>{item.subtitle}</strong> {item.text}</p>
                    {item.list && (
                      <ul>
                        {item.list.map((listItem, listIndex) => (
                          <li key={listIndex}>{listItem}</li>
                        ))}
                      </ul>
                    )}
                    {item.additionalText && <p>{item.additionalText}</p>}
                  </div>
                );
              }
              return null;
            })}
          </div>
        ))}
      </div>
      </div>
    </>
  );
};

export default Terms;